import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getTeam, inviteMember, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Users, Mail, Crown, UserPlus, RefreshCw, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CustomerAssurance } from "@/components/CustomerAssurance";

export default function Team() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["team"], queryFn: getTeam });
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const invite = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await inviteMember(email);
      toast.success(r.message);
      setEmail("");
      qc.invalidateQueries({ queryKey: ["team"] });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send invite");
    } finally {
      setSending(false);
    }
  };

  if (isLoading) return <div className="text-center py-20 font-mono text-sm text-zinc-400">Loading…</div>;

  const { team, members = [], pending_invites = [], is_owner } = data || {};

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#2F6DA3] mb-1">Your workspace</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter flex items-center gap-3">
          <Users size={36} className="text-[#2F6DA3]" /> {team?.name}
        </h1>
        <p className="text-sm text-zinc-500 mt-2">Manage who has access to your team's contract workspace.</p>
      </div>

      {is_owner && (
        <div className="border-2 border-[#14304F] bg-white p-5" data-testid="invite-panel">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus size={16} className="text-[#2F6DA3]" />
            <span className="font-display text-base font-bold tracking-tight">Invite a team member</span>
          </div>
          <form onSubmit={invite} className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="invite-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="colleague@company.co.uk"
              className="flex-1 border-2 border-zinc-300 focus:border-[#2F6DA3] focus:outline-none px-4 py-3 text-sm"
            />
            <Button type="submit" disabled={sending} data-testid="send-invite" className="bg-[#2F6DA3] hover:bg-[#234E74] text-white font-bold uppercase tracking-wider px-6">
              {sending ? <RefreshCw size={15} className="mr-2 animate-spin" /> : <Mail size={15} className="mr-2" />} Send invite
            </Button>
          </form>
          <p className="text-xs text-zinc-400 mt-2">They'll get an email with a link to set a password and join {team?.name}.</p>
        </div>
      )}

      <div className="border-2 border-zinc-950 bg-white" data-testid="members-list">
        <div className="px-5 py-3 border-b-2 border-zinc-100 text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">
          Members ({members.length})
        </div>
        {members.map((m) => (
          <div key={m.id} data-testid={`member-${m.id}`} className="flex items-center justify-between px-5 py-4 border-b border-zinc-100 last:border-0">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 bg-[#14304F] text-white flex items-center justify-center font-display font-bold text-sm">
                {(m.name || m.email)[0]?.toUpperCase()}
              </div>
              <div>
                <div className="font-bold text-sm">{m.name || "—"}</div>
                <div className="text-xs text-zinc-500 font-mono">{m.email}</div>
              </div>
            </div>
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${m.role === "owner" ? "bg-amber-400 text-zinc-900" : "bg-zinc-100 text-zinc-600 border border-zinc-300"}`}>
              {m.role === "owner" ? <><Crown size={11} /> Owner</> : "Member"}
            </span>
          </div>
        ))}
      </div>

      {is_owner && user?.platform_role !== "super_admin" && <CustomerAssurance />}

      {pending_invites.length > 0 && (
        <div className="border-2 border-zinc-200 bg-white" data-testid="pending-invites">
          <div className="px-5 py-3 border-b-2 border-zinc-100 text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">
            Pending invitations ({pending_invites.length})
          </div>
          {pending_invites.map((i) => (
            <div key={i.email} className="flex items-center justify-between px-5 py-3 border-b border-zinc-100 last:border-0">
              <div className="text-sm font-mono text-zinc-600">{i.email}</div>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1"><Clock size={11} /> Invited</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
